import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge, Button, Card, DateInput, Field, Modal, Pagination, Select } from '@/components/ui';

interface AuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  admin: { id: string; email: string; fullName: string } | null;
}

interface AuditPage {
  items: AuditEntry[];
  total: number;
  page: number;
  pages: number;
  filters: { entities: string[]; actions: string[] };
}

const ENTITY_TITLES: Record<string, string> = {
  admin: 'Сотрудник',
  role: 'Роль',
  customer: 'Клиент',
  category: 'Категория',
  product: 'Товар',
  accessory: 'Аксессуар',
  size: 'Размер',
  color: 'Цвет',
  brand: 'Бренд',
  banner: 'Акция',
  promo_code: 'Промокод',
  order: 'Заказ',
  payment: 'Платёж',
  return: 'Возврат',
  offer: 'Оферта',
  settings: 'Настройки',
  setting: 'Настройка',
  pickup_point: 'Пункт выдачи',
  media: 'Файл',
  damage_type: 'Тип повреждения',
};

const ACTION_TITLES: Record<string, string> = {
  create: 'создание',
  update: 'изменение',
  delete: 'удаление',
  move: 'перенос',
  reorder: 'сортировка',
  publish: 'публикация',
  block: 'блокировка',
  unblock: 'разблокировка',
  reset_password: 'сброс пароля',
  upload: 'загрузка',
  set_variants: 'правка вариаций',
  transition: 'смена статуса',
};

/** Удаление и блокировки выделяем: это то, что чаще всего ищут в журнале. */
const ALARMING = new Set(['delete', 'block', 'reset_password']);

export default function AuditPage() {
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [opened, setOpened] = useState<AuditEntry | null>(null);

  const log = useQuery({
    queryKey: ['audit', entity, action, from, to, page, limit],
    queryFn: async () =>
      (
        await api.get<AuditPage>('/admin/audit', {
          params: {
            entity: entity || undefined,
            action: action || undefined,
            from: from || undefined,
            to: to || undefined,
            page,
            limit,
          },
        })
      ).data,
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setEntity('');
    setAction('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Журнал действий</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Кто и что менял в панели. Записи не редактируются и не удаляются
          {log.data ? ` · всего ${log.data.total}` : ''}
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Раздел">
          <Select
            value={entity}
            onChange={(e) => {
              setEntity(e.target.value);
              setPage(1);
            }}
            className="w-48"
          >
            <option value="">Все</option>
            {(log.data?.filters.entities ?? []).map((value) => (
              <option key={value} value={value}>
                {ENTITY_TITLES[value] ?? value}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Действие">
          <Select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="w-48"
          >
            <option value="">Все</option>
            {(log.data?.filters.actions ?? []).map((value) => (
              <option key={value} value={value}>
                {ACTION_TITLES[value] ?? value}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="С даты">
          <DateInput
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="w-40"
          />
        </Field>

        <Field label="По дату">
          <DateInput
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="w-40"
          />
        </Field>

        <Button variant="secondary" onClick={reset}>
          Сбросить
        </Button>
      </div>

      <Card>
        {log.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : !log.data || log.data.items.length === 0 ? (
          <p className="text-sm text-neutral-500">Записей не нашлось</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Когда</th>
                  <th className="pb-2 font-medium">Кто</th>
                  <th className="pb-2 font-medium">Что</th>
                  <th className="pb-2 font-medium">Объект</th>
                  <th className="pb-2 font-medium">IP</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {log.data.items.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-t border-neutral-100 dark:border-neutral-800"
                  >
                    <td className="py-2 whitespace-nowrap text-xs text-neutral-500">
                      {new Date(entry.createdAt).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="py-2">
                      {entry.admin ? (
                        <>
                          <span className="block">{entry.admin.fullName}</span>
                          <span className="block text-xs text-neutral-400">
                            {entry.admin.email}
                          </span>
                        </>
                      ) : (
                        <span className="text-neutral-400">удалённый сотрудник</span>
                      )}
                    </td>
                    <td className="py-2">
                      {ALARMING.has(entry.action) ? (
                        <Badge tone="red">{ACTION_TITLES[entry.action] ?? entry.action}</Badge>
                      ) : (
                        <span>{ACTION_TITLES[entry.action] ?? entry.action}</span>
                      )}
                    </td>
                    <td className="py-2">
                      <span>{ENTITY_TITLES[entry.entity] ?? entry.entity}</span>
                      {entry.entityId && (
                        <span className="block font-mono text-[11px] text-neutral-400">
                          {entry.entityId.slice(0, 18)}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-xs text-neutral-400">{entry.ip ?? '—'}</td>
                    <td className="py-2 text-right">
                      <Button variant="ghost" onClick={() => setOpened(entry)}>
                        Детали
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {log.data && (
        <Pagination
          page={page}
          pages={log.data.pages}
          total={log.data.total}
          limit={limit}
          onPage={setPage}
          onLimit={(next) => {
            setLimit(next);
            setPage(1);
          }}
          sizes={[25, 50, 100]}
        />
      )}

      <Modal open={Boolean(opened)} title="Запись журнала" onClose={() => setOpened(null)}>
        {opened && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Detail label="Когда" value={new Date(opened.createdAt).toLocaleString('ru-RU')} />
              <Detail label="Кто" value={opened.admin?.email ?? 'удалённый сотрудник'} />
              <Detail
                label="Действие"
                value={`${ACTION_TITLES[opened.action] ?? opened.action} · ${ENTITY_TITLES[opened.entity] ?? opened.entity}`}
              />
              <Detail label="IP" value={opened.ip ?? '—'} />
            </div>

            {opened.entityId && (
              <Detail label="Идентификатор объекта" value={opened.entityId} mono />
            )}

            {opened.userAgent && <Detail label="Браузер" value={opened.userAgent} />}

            {Boolean(opened.after) && (
              <div>
                <p className="mb-1 text-xs text-neutral-400">Данные запроса</p>
                <pre className="max-h-64 overflow-auto rounded-lg bg-neutral-100 p-3 text-xs dark:bg-neutral-800">
                  {JSON.stringify(opened.after, null, 2)}
                </pre>
                <p className="mt-1 text-xs text-neutral-400">
                  Пароли, коды и токены в журнал не попадают — они вырезаются при записи
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-neutral-400">{label}</p>
      <p className={`break-words ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  );
}
